/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/indent */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatSelectionListChange } from "@angular/material/list";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { ColumnMode, DatatableComponent, SortDirection, SortType } from "@swimlane/ngx-datatable";
import { get, isEmpty, max, omit, set } from "lodash";
import * as moment from "moment";
import { Observable, of, timer } from "rxjs";
import { debounceTime, distinctUntilChanged, finalize, map, startWith, switchMap, tap } from "rxjs/operators";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { FORMAT } from "../../constants/formats";
import { DEFAULT_QUERY_PARAMS } from "../../constants/services";
import { BoolToStringPipe } from "../../pipes/bool-to-string/bool-to-string.pipe";
import { UserInfo } from "../../types/user-info";
import { getMapOf } from "../../utils/collections/map";
import { getObjectValueByDotNotation } from "../../utils/objects/object";
import { NotificationService } from "../notification/notification.service";
import { PaginatorComponent } from "../paginator/paginator.component";
import { SearchBarComponent } from "../search-bar/search-bar.component";
import {
  DataTable,
  DataTableColumn,
  DataTableParams,
  DataTableParamsOrder,
  DataTableParamsPaginator,
  DataTableParamsWhere,
  GetDataEvent,
  InlineEditControlType,
  OnDatatableEvent,
  PaginatedResponse,
  SearchEvent,
  SearchOption,
  SelectOption,
  UpdateDataEvent,
} from "./data-table.type";

/** @workaround Used as value for the "prop" property of a hidden column.
 * It resolves an issue when updating the dataTable on the reporting view */
export const HIDDEN_COLUMN = "_HIDDEN_COLUMN_";

@Component({
  selector: "app-data-table",
  templateUrl: "./data-table.component.html",
  styleUrls: ["./data-table.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent<T extends { id?: string }> implements OnInit, AfterViewInit {
  @Input() config: DataTable<T> = { id: null, entityName: null };
  @Output() configChange = new EventEmitter<DataTable<T>>();

  /** EventEmitter to get table data */
  @Output() getData = new EventEmitter<GetDataEvent<T>>();

  /** EventEmitter to update table data */
  @Output() updateData = new EventEmitter<UpdateDataEvent<T>>();

  // Components
  @ContentChild("customHeader") customHeader: TemplateRef<any>;
  @ContentChild("customToolBar") customToolBar: TemplateRef<any>;
  @ViewChildren(SearchBarComponent) searchBar: QueryList<SearchBarComponent>;
  @ViewChild(PaginatorComponent) paginator: PaginatorComponent;
  @ViewChild(DatatableComponent) table: DatatableComponent;

  dateFormat = FORMAT.DATE;
  searchOptions: any[] = [];
  inlineSearchFilteredOptions: Observable<any[]>;
  currentUser: UserInfo;
  allRowsSelected: boolean;

  private MAX_AMOUNT_BULK_ACTION = 500;

  /** Used in the template */
  private _allColumns: DataTableColumn<T>[] = [];

  private editing = {};
  private datatableBodyElement: Element;

  private inlineFormGroup: FormGroup;

  private defaultConfig: Partial<DataTable<T>>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  // #region Lifecycle Methods

  ngOnInit(): void {
    this.defaultConfig = {
      cssClass: "material",
      headerHeight: 50,
      bodyHeight: 500,
      footerHeight: 100,
      // rowHeight: 60,
      rowHeight: "auto",
      columns: [],
      sortType: SortType.single,
      columnMode: ColumnMode.force,
      scrollbarH: true,
      scrollbarV: false,
      boxShadow: true,
      initialSearch: true,
      externalPaging: true,
      externalSorting: true,
      loadingIndicator: true,
      reorderable: true,
      resizeOnEditing: true,
      sortValues: [],
      pagination: {
        itemsPerPage: 20,
        itemCount: 0,
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
      },
      toolBar: {
        hidden: false,
        exportCsv: {
          data: null,
          hidden: true,
        },
        selectAllRows: {
          hidden: true,
        },
        refreshTable: {
          hidden: true,
        },
        toggleColumns: {
          hidden: false,
        },
        addItem: {
          hidden: true,
        },
        bulkEdit: {
          hidden: true,
        },
        bulkUnlink: {
          hidden: true,
        },
      },
      disableRows: [],
      selection: {
        hidden: true,
        selectedRows: [],
      },
      summary: {
        height: 60,
        position: "bottom",
        hidden: true,
      },
    };

    /** @workaround to "sync" the dataTable columns */
    this.config.columns.push({
      prop: HIDDEN_COLUMN,
      hidden: true,
    });

    // To avoid unexpected properties overriding we have to remove props with undefined values
    Object.keys(this.config).forEach((key) => this.config[key] === undefined && delete this.config[key]);

    // Set data table
    this.config = {
      ...this.defaultConfig,
      ...this.config,
      cssClass: this.config.cssClass
        ? `${this.defaultConfig.cssClass} ${this.config.cssClass}`
        : `${this.defaultConfig.cssClass}`,
      columns: this.config.columns.map((col) => ({
        ...col,
        width: col.width || 150,
        inlineEdit: {
          ...col.inlineEdit,
          width: 300,
          validators: (row, column, value) => [
            ...(col?.inlineEdit?.validators ? col?.inlineEdit?.validators(row, column, value) : []),
            ...(col?.inlineEdit?.required === false ? [] : [Validators.required]),
          ],
          disabled: (row, column, value, config) =>
            col?.inlineEdit?.disabled
              ? col?.inlineEdit?.disabled?.(row, column, value, config)
              : !this.currentUser.hasRight(["admin", "manager"]),
        },
        pipes: [...(col?.pipes || [])],
      })) as DataTableColumn<T>[],
      toolBar: { ...this.defaultConfig.toolBar, ...this.config.toolBar },
      selection: { ...this.defaultConfig.selection, ...this.config.selection },
      summary: { ...this.defaultConfig.summary, ...this.config.summary },
    };

    if (this.config.defaultColumns) {
      Array.from(new Set(this.config.defaultColumns)).forEach((col) => {
        if (col === "deleted") {
          this.config.columns.push({
            prop: "deleted",
            name: "Deleted",
            pipes: [{ clazz: BoolToStringPipe }],
            hidden: !this.currentUser.isGlobal(),
          });
        }
      });
    }
    this.configChange.emit(this.config);
    this.validateConfig();
  }

  ngAfterViewInit(): void {
    // hide columns
    this.config.columns = this.config.columns.filter((c) => !c.hidden);
    // used for toggle columns
    this._allColumns = this.config.columns.filter((c) => !c.disabledToggle);
    // populate table
    if (this.config.initialSearch !== false) {
      this.search({ isFirstCall: true });
    }
    this.datatableBodyElement = this.table.element.querySelector("datatable-body");

    /** @workaround fix an issue when the columns width are not valid. It force to set the column width after the table is loaded */
    setTimeout(() => this.config.columns.forEach((col) => this.resizeColumnWidthAfterLoad(col, col.$$oldWidth)));

    //To fix Error: NG0100: ExpressionChangedAfterItHasBeenCheckedError
    this.changeDetector.detectChanges();
  }

  // #endregion

  // #region Handlers Methods

  /** On search bar change */
  onSearch(): void {
    this.search({ usingSearchBar: true });
  }

  /** On pagination change */
  onPaginate(): void {
    this.search();
  }

  /** On sort change */
  onSort(): void {
    this.search();
  }

  /** On select all rows of the table handler */
  onSelectAllRows = (): void => {
    this.config.selection.selectedRows = [];
    this.allRowsSelected = !this.allRowsSelected;
  };

  /** On select all rows in the page handler */
  onSelectAllPage = (): void => {
    const isPageSelected = this.isPageSelected();

    if (isPageSelected) {
      this.config.selection.selectedRows = [];
    } else {
      const selectedRowIds = this.config.selection.selectedRows.map((row) => row.id);
      const rowsToSelect = this.config.rows.filter(
        (row) => !selectedRowIds.includes(row.id) && !this.config.disableRows.includes(row.id),
      );
      this.config.selection.selectedRows = [...this.config.selection.selectedRows, ...rowsToSelect];
    }
  };

  /** On select row handler */
  onSelectRow(row: T): void {
    const _row = this.config.rows.find((r) => r.id === row.id);
    const selectedRows = this.config.selection.selectedRows;
    const selectedRowIds = selectedRows.map((selectedRow) => selectedRow.id);

    if (selectedRowIds.includes(_row.id)) {
      this.config.selection.selectedRows = selectedRows.filter((selectedRow) => selectedRow.id !== _row.id);
    } else {
      this.config.selection.selectedRows = [...this.config.selection.selectedRows, _row];
    }
  }

  /** On active cell handler */
  onActivate(event: OnDatatableEvent<T>, columns: DataTableColumn<T>[]): void {
    if (event.type === "dblclick") {
      const column = columns.find((c) => event.column.prop === c.prop);
      const isEntityDeleted = event.row["deleted"];
      if (column?.inlineEdit?.control && this.isRowEnabled(event.row) && !isEntityDeleted) {
        if (!column.inlineEdit.disabled?.(event.row, event.column, event.value, this.config)) {
          this.toggleInlineEdit(event.row, column, event.value, event);
        }
      }
    }
  }

  /** On refresh table handler */
  onRefresh = (search: boolean): void => this.reset(search);

  /** On show/hide column handler */
  onSetColumns = (change: MatSelectionListChange): void => {
    const column = change.option.value;
    const selected = change.option.selected;

    this.toggleColumns(column, selected);
  };

  // #endregion

  // #region Populate Table Methods

  /** Emit an event to get data */
  search(event?: SearchEvent<T>): void {
    const syncWithQueryParams = this.config.syncWithQueryParams;

    if (event?.usingSearchBar) {
      this.paginator?.clear();
    }

    // Search Bar (aka "Where")
    const where = this.getSearchBarValues();

    // Paginator (aka "Pagination")
    const pagination: DataTableParamsPaginator = this.getPaginatorValues(event?.usingSearchBar);

    if (event?.isDeletingOrUnlinking) {
      if (this.config.rows.length === 1 && pagination.page > 1) {
        pagination.page--;
      }

      if (event?.rows) {
        const rowsToDeleteOrUnlink = event.rows.map((row) => row.id) || [];
        this.config.selection.selectedRows = this.config.selection.selectedRows.filter(
          (selectedRows) => !rowsToDeleteOrUnlink.includes(selectedRows.id),
        );
      }
    }

    // Sort (aka "Order")
    this.setSort(syncWithQueryParams, event);
    const order: DataTableParamsOrder<T> = this.config.sortValues;

    const params: DataTableParams<T> = {
      where,
      pagination,
      order,
    };

    if (syncWithQueryParams) {
      this.syncWithQueryParams({ pagination });
    }

    this._search(params);
  }

  // #endregion

  // #region Inline Edit Methods

  toggleInlineEdit(row: T, column: DataTableColumn<T>, value: unknown, event: OnDatatableEvent<T>): void {
    const key = `${row.id}-${column.prop}`;

    let controlValue = value; // set the default value as control value

    // Set control value depending the control type
    if (column.inlineEdit.control === "select") {
      controlValue = column.inlineEdit.select.options?.find((opt) => opt.value === value);
    }

    if (column.inlineEdit.control === "search") {
      const relationId = `${column.prop.toString().split(".")[0]}.id`;
      const id = getObjectValueByDotNotation(row, relationId);
      const _value = getObjectValueByDotNotation(row, column.prop.toString());
      controlValue = { id, name: _value };
    }

    // Create control
    this.inlineFormGroup = this.formBuilder.group({
      [key]: [controlValue, [...(column.inlineEdit?.validators?.(row, column, value) || [])]],
    });

    // Update columns (disable/enable columns)
    const prevColumn = Object.keys(this.editing)?.[0]?.split("-")?.pop();
    if (prevColumn) {
      this.cancelInlineEdit(
        row,
        this.config.columns.find((col) => col.prop === prevColumn),
      );
    }
    this.editing = { [key]: true };

    this.resizeColumnWidth(column, column.inlineEdit.width);

    if (column.inlineEdit.control === "search") {
      const propToDisplay = column.inlineEdit.search.propToDisplay;
      this.inlineSearchFilteredOptions = this.inlineFormGroup.controls[key].valueChanges.pipe(
        tap(() => (this.config.loadingIndicator = true)),
        startWith(""),
        distinctUntilChanged(),
        debounceTime(400),
        switchMap((formValue) => {
          const isValidString =
            this.inlineFormGroup.status === "VALID" && typeof formValue === "string" && !isEmpty(formValue);
          const forceFetchData = isEmpty(formValue) && column.inlineEdit.search.fetchDefaultData;
          if (isValidString || forceFetchData) {
            return column.inlineEdit.search.service(formValue, row).pipe(
              map((response: PaginatedResponse<T>) => {
                const options = response.items.map((item) => ({ id: item.id, [propToDisplay]: item[propToDisplay] }));
                this.searchOptions = options;
                return options;
              }),
            );
          } else {
            return of(null);
          }
        }),
        tap(() => (this.config.loadingIndicator = false)),
        finalize(() => (this.config.loadingIndicator = false)),
      );
    }

    // Mark as touched to sync the line below the input (when it has an error it will paint in red)
    this.inlineFormGroup.controls[key].markAsTouched();

    // Set focus to "input" control
    setTimeout(() => ((event as any)?.cellElement as HTMLElement)?.querySelector("input")?.focus());
  }

  updateInlineEdit(row: T, column: DataTableColumn<T>): void {
    const key = `${row.id}-${column.prop}`;

    if (this.inlineFormGroup.controls[key].invalid) {
      return;
    }

    const controlType = column.inlineEdit.control;
    const controlValue: string | moment.Moment | SelectOption | SearchOption = this.inlineFormGroup.value[key];

    const propertyToUpdate = (column?.inlineEdit?.search?.columnToUpdate || (column.prop as keyof T)) as string;

    const updatedValues: Partial<T> = {};

    if (controlType === "input") {
      // For this case, we don't need to worry about "number" type
      // cause they are sent as "string" type but the backend automatically converts those as "number" type
      updatedValues[propertyToUpdate] = controlValue;
    } else if (controlType === "date") {
      // For this case, we don't need to do a ToString() of the Moment object
      // cause it is automatically serialized
      updatedValues[propertyToUpdate] = controlValue;
    } else if (controlType === "select") {
      updatedValues[propertyToUpdate] = (controlValue as SelectOption)?.value ?? null;
    } else if (controlType === "search") {
      updatedValues[propertyToUpdate] = (controlValue as SearchOption)?.id ?? null;
    }

    this.updateCell(row, column, updatedValues, controlValue);
  }

  cancelInlineEdit(row: T, column: DataTableColumn<T>): void {
    const key = `${row.id}-${column.prop}`;
    this.editing = omit(this.editing, [key]);
    this.resizeColumnWidth(column);
  }

  /** Resize column width when entering/exiting edit mode */
  resizeColumnWidth(column: DataTableColumn<T>, newWidth?: number): void {
    if (!this.config.resizeOnEditing) {
      return;
    }

    const internalColumn = this.table._internalColumns.find((col) => col.prop === column.prop);
    const width = !newWidth
      ? column.inlineEdit.oldWidth
      : max([column.inlineEdit.oldWidth, newWidth, internalColumn.$$oldWidth]);

    this.table.onColumnResize({ column: internalColumn, newValue: width });
    this.table.recalculateColumns();

    column.inlineEdit.oldWidth = internalColumn.$$oldWidth;

    //Scroll to element to be updated
    setTimeout(() => {
      const element = document.getElementById("scroll" + column.prop);
      if (element) element.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, 100);
  }

  /** Resize column width. Used after the table is loaded */
  resizeColumnWidthAfterLoad(column: DataTableColumn<T>, width: number): void {
    const internalColumn = this.table._internalColumns.find((col) => col.prop === column.prop);
    this.table.onColumnResize({ column, newValue: width });
    this.table.onColumnResize({ column: internalColumn, newValue: width });
    this.table.recalculateColumns();
  }

  // #endregion

  // #region Other Methods

  /** Return the row id */
  getRowId = (row: T): string => row.id;

  /** Check if row is enabled */
  isRowEnabled = (row: T): boolean => {
    if (!row?.id || this.allRowsSelected) {
      return false;
    }
    return !this.config.disableRows?.includes(row?.id);
  };

  /** Check if row is selected */
  isRowSelected = (row: T): boolean => {
    if (this.allRowsSelected) {
      return true;
    }

    const selectedRowIds = this.config.selection.selectedRows.map((selectedRow) => selectedRow.id);
    return selectedRowIds.includes(row.id);
  };

  /** Check if page is selected */
  isPageSelected = (): boolean => {
    if (this.allRowsSelected && !!this.config.rows.length) {
      return true;
    }

    const enabledRowIds = this.getEnabledRows().map((row) => row.id);
    const selectedRowIds = this.getSelectedRows().map((row) => row.id);
    return !!enabledRowIds?.length && enabledRowIds?.every((id) => selectedRowIds.includes(id));
  };

  /** Check if some but not all rows are selected */
  isIndeterminate = (): boolean => {
    const selectedRowIds = this.getSelectedRows().map((row) => row.id);
    return (
      (!!selectedRowIds.length || (this.allRowsSelected && !!this.getEnabledRows().length)) && !this.isPageSelected()
    );
  };

  getEnabledRows = (): T[] => this.config.rows?.filter((row) => !this.config.disableRows.includes(row.id)) || [];

  getSelectedRows = (): T[] => this.config.selection.selectedRows || [];

  /** Check if can edit the row */
  canEdit = (row: T, col: DataTableColumn<T>): boolean => {
    return this.isRowEnabled(row) && !this.editing[row.id + "-" + col.prop];
  };

  /** Check if can do a bulk action with the selected rows */
  canBulkAction = (): boolean => {
    return (
      (this.config.selection.selectedRows.length || (this.allRowsSelected && this.config.pagination.totalItems)) &&
      !this.isThereMoreSelectedRowsThanTheLimit()
    );
  };

  /** Check if there more selected rows than the limit */
  isThereMoreSelectedRowsThanTheLimit = (): boolean => {
    return this.getSelectedRowsCount() > this.MAX_AMOUNT_BULK_ACTION;
  };

  /** Get the number of enabled rows */
  getEnabledRowsCount = (): number => {
    return this.getEnabledRows().length;
  };

  /** Get the number of selected rows */
  getSelectedRowsCount = (): number => {
    return this.allRowsSelected ? this.config.pagination.totalItems : this.getSelectedRows().length;
  };

  /** Get the message for the bulk action tooltip when the number of selected rows is greater than allowed */
  getBulkActionMessageWhenItsDisabled = (): string => {
    return this.isThereMoreSelectedRowsThanTheLimit()
      ? `The number of selected rows is greater than allowed (${this.getSelectedRowsCount()}/${
          this.MAX_AMOUNT_BULK_ACTION
        })`
      : "";
  };

  /** Get the message for the checkbox */
  getCheckboxMessageWhenItsDisabled = (): string => {
    // eslint-disable-next-line @typescript-eslint/quotes
    return this.allRowsSelected ? 'Uncheck the "Select All" checkbox to use it manually' : "";
  };

  /** Return the search values or an empty object */
  getSearchBarValues = (): DataTableParamsWhere<T> | DataTableParamsWhere<T>[] =>
    (this.searchBar?.first?.getValues() as DataTableParamsWhere<T> | DataTableParamsWhere<T>[]) ||
    ({} as DataTableParamsWhere<T>);
  // For now, we don't need the below line
  // getSearchBarValues = (): AnyObject => this.searchBar?.reduce((acc, curr) => ({ ...acc, ...curr.getValues() }), {});

  /** Return the paginator values */
  getPaginatorValues = (resetPage?: boolean): DataTableParamsPaginator => ({
    ...this.paginator?.getValues(),
    ...(resetPage && { page: DEFAULT_QUERY_PARAMS.pagination.page }),
  });

  getSortParam = (direction: "asc" | "desc"): string => `${this.config.id}_${direction}`;

  /** Reset table */
  reset = (search?: boolean): void => {
    // Reset rows
    this.table.rows = [];
    // Reset selection
    this.resetSelectedRows();
    // Reset search bar
    this.searchBar.forEach((sb) => sb.clear(false));
    // Reset paginator
    if (this.paginator) {
      this.paginator.clear(false);
    }
    // Reset query params
    if (this.config.syncWithQueryParams) {
      this.router.navigate(["."], { relativeTo: this.route, queryParams: null });
    }
    // Reset scroll
    this.scrollX(0);
    // Run search
    if (search) {
      const where = {} as DataTableParamsWhere<T> | DataTableParamsWhere<T>[];
      const pagination = this.paginator?.getValues() || {
        page: DEFAULT_QUERY_PARAMS.pagination.page,
        pageSize:
          this.paginator?.pageSizeOptions?.[0] ||
          this.paginator?.defaultPageSizeOptions[0] ||
          DEFAULT_QUERY_PARAMS.pagination.pageSize,
      };
      const order = this.config.sortValues;
      this._search({ where, pagination, order });
    }
  };

  /** Hide/check column */
  toggleColumns(column: DataTableColumn<T>, selected: boolean): void {
    this.config.loadingIndicator = true;

    if (!selected) {
      this.config.columns = this.config.columns.filter((c) => c.$$id !== column.$$id);
    } else {
      this.config.columns = [column, ...this.config.columns];
    }
    this.config.loadingIndicator = false;
  }

  resetScrollbar(): void {
    if (this.table.headerComponent) {
      this.table.headerComponent.offsetX = 0;
    }
    if (this.table.bodyComponent) {
      this.table.bodyComponent.offsetX = 0;
      this.table.bodyComponent.offsetY = 0;
    }
    this.setBodyHeight();
    this.table.recalculate();
  }

  resetSelectedRows(refreshSelectedRows = false): void {
    this.config.selection.selectedRows = [];
    this.table.selected = [];
    this.allRowsSelected = false;
    if (refreshSelectedRows) {
      this.table.recalculate();
    }
  }

  /** Value to be displayed on search inputs */
  searchInputLabel(option: SearchOption): string {
    if (option === null) {
      return null;
    }
    const key = Object.keys(option).find((k) => k !== "id");
    return option[key];
  }

  /** Update the horizontal scrollbar to a specific position or refresh it */
  scrollX = (offSetX: number = this.datatableBodyElement.scrollLeft): void => {
    const scrollLeftMax = this.datatableBodyElement.scrollWidth - this.datatableBodyElement.clientWidth;
    const isValidOffSet = offSetX >= 0 && offSetX <= scrollLeftMax;
    /** @workaround To update cells properly first we update the scroll twice, then we set the correct value */
    this.datatableBodyElement.scrollLeft = 0;
    this.datatableBodyElement.scrollLeft = scrollLeftMax;
    this.datatableBodyElement.scrollLeft = isValidOffSet ? offSetX : scrollLeftMax;
  };

  /** Update the cell of the row */
  private updateCell(
    row: T,
    column: DataTableColumn<T>,
    updatedValues: Partial<T>,
    controlValue: string | moment.Moment | SelectOption | SearchOption,
  ): void {
    const originalRow = { ...row };
    this.updateData.emit({
      row: originalRow,
      updatedValues,
      onResult: (data) => {
        const key = `${row.id}-${column.prop}`;
        // Disable editing mode on the cell
        this.editing = omit(this.editing, [key]);

        const controlType: InlineEditControlType = column?.inlineEdit.control;
        const rows = getMapOf([...this.config.rows], "id") as Map<T["id"], T>;
        const rowToUpdate = rows.get(row.id);
        const propToUpdate = column.inlineEdit.search?.propToUpdate || column.prop;

        // Here we set/update the property value (for the cell being edited), be aware that it set the value by reference,
        // so the table rows (this.config.row) are automatically updated
        if (controlType === "input") {
          set(rowToUpdate, propToUpdate, controlValue);
        } else if (controlType === "date") {
          set(rowToUpdate, propToUpdate, controlValue);
        } else if (controlType === "select") {
          set(rowToUpdate, propToUpdate, (controlValue as SelectOption).value);
        } else if (controlType === "search") {
          // For this case, we overwrite the cell/property to get the displayed label properly updated
          set(rowToUpdate, propToUpdate, controlValue);
        }

        // Here we set/update the rest of the properties values only when the key ("column.prop") exists in data (the response object)
        this.config.columns
          .filter((col) => col.prop !== column.prop)
          .forEach((col) => {
            const value = get(data, col.prop);
            if (value !== undefined) {
              set(rowToUpdate, col.prop, value);
            }
          });

        if (this.config.showNotificationAfterUpdate !== false) {
          this.notificationService.success(`${this.config.entityName} successfully edited`);
        }
        this.resizeColumnWidth(column);
      },
      onError: (error?: Error) => {
        console.error(error);
        const defaultMessage = `Could not update ${column.name}`;
        const errorMessage = error || error?.message || error?.[0] || error?.message?.[0] || defaultMessage;
        const parsedErrorMessage = this.handleErrorMessage(column.name, errorMessage);
        this.notificationService.error(parsedErrorMessage);
        this.inlineFormGroup.setErrors({ backendValidation: parsedErrorMessage });
      },
    });
  }

  /** Emit an event to get data */
  private _search(queryParams?: DataTableParams<T>): void {
    if (!queryParams) {
      const where = this.searchBar.first.getValues() as DataTableParamsWhere<T> | DataTableParamsWhere<T>[];
      const pagination = this.paginator?.getValues() || {
        page: DEFAULT_QUERY_PARAMS.pagination.page,
        pageSize:
          this.paginator?.pageSizeOptions?.[0] ||
          this.paginator?.defaultPageSizeOptions[0] ||
          DEFAULT_QUERY_PARAMS.pagination.pageSize,
      };
      const order = this.config.sortValues;
      queryParams = { where, pagination, order };
    }

    this.config.loadingIndicator = true;
    this.getData.emit({
      queryParams,
      onResult: (data: T[] | PaginatedResponse<T>) => {
        if (this.hasPaginatedResponse(data)) {
          const { items, meta } = data;

          this.config.rows = items;
          this.config.pagination = meta;

          this.validateExportCsvStatus(items);
        } else {
          this.config.rows = data.filter((element) => {
            const where = this.getSearchBarValues();
            const keys = Object.keys(where);
            for (const key of keys) {
              const elementValue = `${element[key]}`;
              // Here the "where" value can be an object like { <key>: { $cont: <value> } } or { <key>: <value> }
              // so we have to parse it and get the correct value
              const whereValue = typeof where[key] === "object" ? `${Object.values(where[key])[0]}` : `${where[key]}`;
              if (!elementValue.toLowerCase().includes(whereValue.toLowerCase())) {
                return false;
              }
            }
            return true;
          });

          if (this.paginator) {
            const currentPage = queryParams.pagination.page || this.config.pagination.currentPage;
            const itemsPerPage = queryParams.pagination.pageSize || this.config.pagination.itemsPerPage;
            const totalItems = this.config.rows.length ?? this.defaultConfig.pagination.totalItems;
            const totalPages = Math.ceil(totalItems / itemsPerPage);
            let itemCount = this.defaultConfig.pagination.itemCount;

            if (currentPage === totalPages) {
              itemCount = totalItems % itemsPerPage || itemsPerPage;
            } else if (currentPage < totalPages) {
              itemCount = itemsPerPage;
            }

            this.config.pagination = {
              currentPage,
              itemsPerPage,
              itemCount,
              totalPages,
              totalItems,
            };

            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemCount;

            this.config.rows = this.config.rows.slice(startIndex, endIndex);
          }

          this.validateExportCsvStatus(data);
        }

        if (this.allRowsSelected) {
          this.config.selection.selectedRows = [];
        }

        this.config.loadingIndicator = false;

        /** @workaround fix an issue when the table is on a pop-up and its columns don't fit with the table width
         * It will dispatch the "resize" event for each time/value (in milliseconds) in the array
         */
        [250, 500, 1000, 1500, 2000, 3000].forEach((value) =>
          setTimeout(() => window.dispatchEvent(new Event("resize")), value),
        );
        this.setBodyHeight();
      },
      onError: () => {
        // TODO handler message
        this.config.loadingIndicator = false;
      },
    });
  }

  /** Enable/disable export CSV button based on item count */
  private validateExportCsvStatus(items: T[]): void {
    if (!this.config.toolBar.exportCsv.hidden) {
      this.config.toolBar.exportCsv = {
        ...this.config.toolBar.exportCsv,
        disabled: isEmpty(items),
      };
    }
  }

  /** Validate the table config */
  private validateConfig(): void {
    for (const column of this.config.columns) {
      const controlType = column.inlineEdit?.control;

      if (!controlType) {
        continue;
      }
      if (controlType === "select") {
        if (!column.inlineEdit.select) {
          throw Error("'select' property is required when control type is 'select'");
        }
      }
      if (controlType === "search") {
        if (!column.inlineEdit.search) {
          throw Error("'search' property is required when control type is 'search'");
        }
      }
    }
  }

  /** Set table body height (rows container height) */
  private setBodyHeight(): void {
    if (!this.config.bodyHeight) {
      return;
    }

    const htmlElement = document.getElementById(`table-${this.config.id}`);

    if (htmlElement) {
      const htmlElement2 = htmlElement.querySelectorAll<HTMLElement>("datatable-body");

      if (htmlElement2) {
        htmlElement2.item(0).style.height = `${this.config.bodyHeight}px`;
      }
    }
  }

  private syncWithQueryParams({ pagination }: DataTableParams = undefined) {
    const searchBarQueryParams = this.searchBar?.reduce((acc, curr) => ({ ...acc, ...curr.getQueryParams() }), {});
    const paginatorQueryParams = this.paginator?.getQueryParams(pagination);
    const sortQueryParams = this.config.sortQueryParams;

    const queryParams: Params = {
      ...(searchBarQueryParams || {}),
      ...(paginatorQueryParams || {}),
      ...(sortQueryParams || {}),
    };

    this.updateQueryParams(queryParams);
  }

  private updateQueryParams(queryParams: Params) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  private setSort(syncWithQueryParams: boolean, event: SearchEvent<T>): void {
    if (syncWithQueryParams && !isEmpty(this.route.snapshot.queryParams) && event?.isFirstCall) {
      const sortAsc = this.route.snapshot.queryParamMap.get(this.getSortParam(SortDirection.asc));
      const sortDesc = this.route.snapshot.queryParamMap.get(this.getSortParam(SortDirection.desc));

      if (sortAsc || sortDesc) {
        const orderBy = {
          dir: sortAsc ? SortDirection.asc : sortDesc ? SortDirection.desc : undefined,
          prop: sortAsc || sortDesc || undefined,
        };

        this.table.sorts = [orderBy];
        this.config.sortValues = {
          [orderBy.prop]: `${orderBy.dir}`,
        };
      }
    }

    if (this.table?.sorts.length && this.config.sortType === SortType.single) {
      const { dir, prop } = this.table.sorts[0];
      this.config.sortValues = {
        [prop]: `${dir}`,
      };
      if (syncWithQueryParams) {
        this.config.sortQueryParams = {
          ...{ [this.getSortParam("asc")]: dir === "asc" ? prop : undefined },
          ...{
            [this.getSortParam("desc")]: dir === "desc" ? prop : undefined,
          },
        };
      }
    }
  }

  private delay(seconds: number): Observable<number> {
    return timer(seconds);
  }

  private hasPaginatedResponse(data: T[] | PaginatedResponse<T>): data is PaginatedResponse<T> {
    return !!(data as PaginatedResponse<T>).items && !!(data as PaginatedResponse<T>).meta;
  }

  /** Handle error messages to be displayed to the user */
  private handleErrorMessage(columnName: string, error: string): string {
    const errorsMapping = {
      "must be an UUID": "not valid",
    };

    for (const key of Object.keys(errorsMapping)) {
      const _error = error?.toString();
      if (_error.includes(key)) {
        return `${columnName} ${errorsMapping[key]}`;
      }
    }

    return error;
  }

  // #endregion
}
